import { useEffect, useMemo, useState } from 'react';
import { AvailabilitySlotsDocument, AvailabilitySlotsQueryResult } from '@/types-aggregatore';
import { getStartOfDayTime, getStartOfDayUtcTime } from '@/app/_components/widgets/listing/_utils/time';
import client from '@/utils/apolloClient';
import { useIsWelfare } from '@/app/_hooks/useIsWelfare';

interface UseAvailabilitySlotsProps {
  specialityId: string;
  workgroupId: string;
  enabled?: boolean;
  firstAvailabilityDate?: number | null;
  selectedDoctorId?: string;
  /** If > 0, minimum startDate for requests is now + delayAvailability (ms) */
  delayAvailability?: number;
}

type AvailabilitySlots = AvailabilitySlotsQueryResult['availabilitySlots'];
type AvailabilitySlotsVariables = {
  specialityId: string;
  doctorId?: string;
  workgroupId: string;
  isWelfareRequest: true | undefined;
  startDate: number | undefined;
};

const fetchAvailabilitySlots = async (variables: AvailabilitySlotsVariables) => {
  const { data: { availabilitySlots = [] } = {} } = await client.query({
    query: AvailabilitySlotsDocument,
    variables,
  });

  return availabilitySlots;
};

export const useAvailabilitySlots = ({
  specialityId,
  workgroupId,
  enabled = true,
  firstAvailabilityDate,
  selectedDoctorId,
  delayAvailability,
}: UseAvailabilitySlotsProps) => {
  const isWelfare = useIsWelfare();

  const [isInitialLoading, setIsInitialLoading] = useState(true);
  const [isLoadingMore, setIsLoadingMore] = useState(false);
  const [allData, setAllData] = useState<AvailabilitySlots>([]);
  const [hasMoreData, setHasMoreData] = useState(true);

  const [prevParams, setPrevParams] = useState({ specialityId, workgroupId, selectedDoctorId });
  if (
    prevParams.specialityId !== specialityId ||
    prevParams.workgroupId !== workgroupId ||
    prevParams.selectedDoctorId !== selectedDoctorId
  ) {
    setPrevParams({ specialityId, workgroupId, selectedDoctorId });
    setIsInitialLoading(true);
    setAllData([]);
  }

  useEffect(() => {
    if (!enabled) return;
    let isCancelled = false;

    const fetchInitialData = async () => {
      try {
        // ! delayAvailability need only when there is a request for today's date
        const minStartDate = delayAvailability ? new Date().getTime() + delayAvailability : undefined;
        let availabilitySlots = await fetchAvailabilitySlots({
          startDate: minStartDate,
          specialityId,
          doctorId: selectedDoctorId,
          workgroupId,
          isWelfareRequest: isWelfare || undefined,
        });

        // Algolia's first availability can be stale (today or in the past) and the first request already covers it:
        // retry only from tomorrow on and after the booking delay
        const retryStartDate = firstAvailabilityDate ? getStartOfDayUtcTime(firstAvailabilityDate) : null;
        const tomorrowStartDate = getStartOfDayUtcTime(Date.now(), 1);

        if (
          availabilitySlots.length === 0 &&
          retryStartDate &&
          retryStartDate >= tomorrowStartDate &&
          retryStartDate > (minStartDate ?? 0)
        ) {
          availabilitySlots = await fetchAvailabilitySlots({
            startDate: retryStartDate,
            specialityId,
            doctorId: selectedDoctorId,
            workgroupId,
            isWelfareRequest: isWelfare || undefined,
          });
        }

        if (!isCancelled) {
          setHasMoreData(availabilitySlots.length > 0);
          setAllData(availabilitySlots);
        }
      } catch (error) {
        if (!isCancelled) {
          console.error('Error on initial fetching availability slots:', error);
        }
      } finally {
        if (!isCancelled) {
          setIsInitialLoading(false);
        }
      }
    };

    setIsInitialLoading(true);
    setAllData([]);
    fetchInitialData();

    return () => {
      isCancelled = true;
    };
  }, [specialityId, selectedDoctorId, workgroupId, enabled, firstAvailabilityDate, isWelfare, delayAvailability]);

  const fetchNextPage = async () => {
    if (allData.length === 0) return;
    // Get the last slot's day and add 1 day
    const nextDayAsMillis = getStartOfDayUtcTime(allData[allData.length - 1].start, 1);

    if (isLoadingMore || !enabled) return;

    setIsLoadingMore(true);

    try {
      const availabilitySlots = await fetchAvailabilitySlots({
        startDate: nextDayAsMillis,
        specialityId,
        doctorId: selectedDoctorId,
        workgroupId,
        isWelfareRequest: isWelfare || undefined,
      });

      setHasMoreData(availabilitySlots.length > 0);
      setAllData(prev => [...prev, ...availabilitySlots]);
    } catch (error) {
      console.error('Error fetching availability slots:', error);
    } finally {
      setIsLoadingMore(false);
    }
  };

  const groupedDays = useMemo(() => {
    if (!allData.length) return {};

    const nowAsMilliseconds = new Date().getTime();
    const daysByDate: Record<string, Record<number, NonNullable<AvailabilitySlots>[number]>> = {};

    for (const slot of allData) {
      /**
       *  ? For unknown reasons, availability slots query, sometimes returns slots with a start date in the past
       *
       *  For a query at 15:52 it returned a slot with a start date of 11:40, on 11 september 2025
       */
      if (!slot || slot.start < nowAsMilliseconds) continue;

      const dayKey = getStartOfDayTime(slot.start);
      const startKey = slot.start;

      const slotsByStart = (daysByDate[dayKey] ||= {});
      const existing = slotsByStart[startKey];

      if (!existing || (slot.price ?? 0) < (existing.price ?? 0)) {
        slotsByStart[startKey] = slot;
      }
    }

    return Object.entries(daysByDate).reduce<Record<string, NonNullable<AvailabilitySlots>>>((acc, [dayKey, slotsByStart]) => {
      acc[dayKey] = Object.values(slotsByStart);
      return acc;
    }, {});
  }, [allData]);

  const sortedDates = useMemo(() => Object.keys(groupedDays).map(Number).sort(), [groupedDays]);

  return {
    allSlots: allData,
    groupedDays,
    sortedDates,
    isInitialLoading,
    isLoadingMore,
    fetchNextPage,
    hasMoreData,
  };
};
